import React, { FC } from "react";
import { ProductDataType } from "data/types";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import SafeImage from "components/SafeImage/SafeImage";
import { getFirstImageUrl } from "utils/imageUtils";

export interface ProductCardProps {
  className?: string;
  data?: ProductDataType;
  size?: "default" | "small";
}

const ProductCard: FC<ProductCardProps> = ({
  size = "default",
  className = "",
  data,
}) => {
  if (!data) return null;

  const {
    SKU,
    name,
    finishes,
    colors,
    image_urls,
    specifications,
    subcategory,
  } = data;

  const imageUrl = getFirstImageUrl(image_urls);

  const material = specifications?.[0]?.material;
  const finishList = finishes
    ? finishes.split(",").map((f) => f.trim()).filter(Boolean).slice(0, 2)
    : [];
  const colorList = colors
    ? colors.split(",").map((c) => c.trim()).filter(Boolean).slice(0, 2)
    : [];
  const tags = [...finishList, ...colorList].slice(0, 3);

  return (
    <article
      className={`nc-ProductCard group relative flex flex-col h-full rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden hover:shadow-bcr-lg hover:border-primary-300/60 dark:hover:border-primary-600/40 transition-all duration-300 ${className}`}
    >
      <Link
        to={`details/${SKU}`}
        relative="path"
        className="flex flex-col h-full"
      >
        {/* Image */}
        <div className="relative aspect-[4/3] max-h-[200px] bg-neutral-50 dark:bg-neutral-800/60 overflow-hidden">
          <SafeImage
            src={imageUrl}
            alt={name}
            placeholderVariant="product"
            placeholderCompact
            objectFit="contain"
            imgClassName="p-4 sm:p-5 group-hover:scale-[1.03] transition-transform duration-500"
          />
          <span className="absolute top-3 left-3 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-[#0f1b3d]/90 text-white/90 backdrop-blur-sm">
            {SKU}
          </span>
          {subcategory?.name && (
            <span className="absolute top-3 right-3 px-2 py-1 rounded-md text-[10px] font-medium bg-white/90 dark:bg-neutral-900/90 text-neutral-600 dark:text-neutral-300 backdrop-blur-sm border border-neutral-200/80 dark:border-neutral-700 line-clamp-1 max-w-[45%]">
              {subcategory.name}
            </span>
          )}
        </div>

        {/* Content */}
        <div
          className={`flex flex-col flex-1 ${
            size === "default" ? "p-5" : "p-4"
          }`}
        >
          <h2
            className={`font-semibold text-neutral-900 dark:text-white leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors ${
              size === "default" ? "text-base sm:text-lg" : "text-sm"
            }`}
            title={name}
          >
            <span className="line-clamp-2">{name}</span>
          </h2>

          {material && (
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Material:{" "}
              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                {material}
              </span>
            </p>
          )}

          {tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-[11px] font-medium rounded-md bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="mt-auto pt-4 flex items-center justify-between border-t border-neutral-100 dark:border-neutral-800">
            <span className="text-sm font-semibold text-primary-600 dark:text-primary-400 group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">
              View details
            </span>
            <span className="w-8 h-8 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center group-hover:bg-primary-600 group-hover:text-white transition-colors">
              <ArrowRightIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 group-hover:text-white transition-colors" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default ProductCard;
